import { ComponentDesignUploadFile, UploadFilesResult } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUserOrganization } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { componentDesignsSlice } from '../store/componentDesignsSlice';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

export function loadComponentDesignsIfNeeded(componentDesignGuids: string[]): AsyncThunkActionVoid {
  return async function loadComponentDesignsIfNeededThunkAction(dispatch, getState) {
    const state = getState();

    const missingComponentDesignGuids = componentDesignGuids.filter(
      (guid) => state.componentDesigns.data[guid] === undefined && !state.componentDesigns.designsBeingLoaded[guid],
    );

    if (missingComponentDesignGuids.length > 0) {
      await dispatch(loadComponentDesigns(missingComponentDesignGuids));
    }
  };
}

export function loadComponentDesigns(componentDesignGuids: string[]): AsyncThunkActionVoid {
  return async function loadComponentDesignsThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Loading component designs...'));

    try {
      dispatch(componentDesignsSlice.actions.beganLoading(componentDesignGuids));

      const componentDesigns = await designService.componentDesigns.bulkLoad(componentDesignGuids);

      dispatch(componentDesignsSlice.actions.loadedFromServer(componentDesigns));
    } catch (error) {
      console.error(error);

      dispatch(componentDesignsSlice.actions.failedToLoad(componentDesignGuids));
      dispatch(enqueueError('Unable to load component designs.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

/**
 * Does not dispatch success/failure message snackbars
 */
export function uploadComponentDesigns(
  files: ComponentDesignUploadFile[],
): AsyncThunkActionReturn<UploadFilesResult | null> {
  return async function uploadComponentDesignsThunkAction(dispatch, getState) {
    const loggedInUserOrganization = getLoggedInUserOrganization(getState());
    if (!loggedInUserOrganization) throw new Error('Must be logged in to upload component designs');
    const spinnerId = dispatch(addSpinnerMessage(`Uploading component designs...`));

    try {
      const uploadFilesResult = await designService.componentDesigns.postUploadFiles(
        files,
        loggedInUserOrganization,
        null,
      );
      dispatch(
        componentDesignsSlice.actions.loadedFromServer(
          uploadFilesResult.componentDesignsWithTimestamps.map(({ componentDesign }) => componentDesign),
        ),
      );
      return uploadFilesResult;
    } catch (error) {
      console.log(error);
      return null;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
